import type {
  AskedQuestion,
  AskedQuestionQueue,
  Quiz,
  QuizResults,
  Screen,
  SessionResults,
  SessionSummary,
  Theme,
} from '@floorpoll/shared';
import { apiRequest } from './api.js';

/** What an uploaded image is for — the theme's two (#19) and the poll's own (#75). */
export type AssetKind = 'logo' | 'background' | 'question' | 'option';

/** One place for the quiz endpoints, so pages do not hand-build URLs. */
export const quizApi = {
  get: (quizId: string) => apiRequest<{ quiz: Quiz }>(`/quizzes/${quizId}`).then((r) => r.quiz),

  update: (quizId: string, fields: { title?: string; theme?: Theme }) =>
    apiRequest<{ quiz: Quiz }>(`/quizzes/${quizId}`, { method: 'PATCH', body: fields }).then(
      (r) => r.quiz,
    ),

  saveStructure: (quizId: string, screens: Screen[]) =>
    apiRequest<{ quiz: Quiz }>(`/quizzes/${quizId}/structure`, {
      method: 'PUT',
      body: { screens },
    }).then((r) => r.quiz),

  /** Story #48 — returns the copy, which is always a fresh draft. */
  duplicate: (quizId: string) =>
    apiRequest<{ quiz: Quiz }>(`/quizzes/${quizId}/duplicate`, { method: 'POST' }).then(
      (r) => r.quiz,
    ),

  remove: (quizId: string) => apiRequest<void>(`/quizzes/${quizId}`, { method: 'DELETE' }),

  results: (quizId: string) =>
    apiRequest<{ results: QuizResults }>(`/quizzes/${quizId}/results`).then((r) => r.results),

  resetResults: (quizId: string) =>
    apiRequest<void>(`/quizzes/${quizId}/results`, { method: 'DELETE' }),

  /** Story #46 — the poll's sealed runs, newest first. */
  sessions: (quizId: string) =>
    apiRequest<{ sessions: SessionSummary[] }>(`/quizzes/${quizId}/sessions`).then(
      (r) => r.sessions,
    ),

  /** Story #46 — one run, addressed without its poll: a run outlives it. */
  sessionResults: (sessionId: string) =>
    apiRequest<{ results: SessionResults }>(`/sessions/${sessionId}`).then((r) => r.results),

  startPresentation: (quizId: string) =>
    apiRequest<{ quiz: Quiz }>(`/quizzes/${quizId}/presentation`, { method: 'POST' }).then(
      (r) => r.quiz,
    ),

  endPresentation: (quizId: string) =>
    apiRequest<{ quiz: Quiz }>(`/quizzes/${quizId}/presentation`, { method: 'DELETE' }).then(
      (r) => r.quiz,
    ),

  goToScreen: (quizId: string, index: number) =>
    apiRequest<{ quiz: Quiz }>(`/quizzes/${quizId}/presentation/screen`, {
      method: 'POST',
      body: { index },
    }).then((r) => r.quiz),

  /**
   * Holds or reveals the results, and closes or reopens voting (#49).
   *
   * A partial patch, matching the route: send only the flag being moved so the
   * other keeps whatever the presenter last set it to.
   */
  setPresentationControls: (
    quizId: string,
    controls: { resultsVisible?: boolean; votingClosed?: boolean; qandaOpen?: boolean },
  ) =>
    apiRequest<{ quiz: Quiz }>(`/quizzes/${quizId}/presentation/controls`, {
      method: 'POST',
      body: controls,
    }).then((r) => r.quiz),

  /**
   * Audience Q&A (#118) — the console half.
   *
   * The audience's own calls are not here: they go to `/public/join/{code}`
   * unauthenticated, straight from the taker page, because a phone holds no
   * token and must never be handed a quizId.
   */

  /** The moderator's three columns, plus what is on the projector. */
  questions: (quizId: string) =>
    apiRequest<{ queue: AskedQuestionQueue; presentedAskedQuestionId: string | null }>(
      `/quizzes/${quizId}/questions`,
    ),

  /**
   * Approves a question, optionally placing it in the running order.
   *
   * `order` is sent only when the caller means to set one. Approving without
   * it appends — `byPresentationOrder` sorts an unordered question after every
   * ordered one, so a plain approve never jumps the queue.
   */
  approveQuestion: (quizId: string, askedQuestionId: string, order?: number) =>
    apiRequest<{ question: AskedQuestion }>(
      `/quizzes/${quizId}/questions/${askedQuestionId}/approve`,
      { method: 'PUT', body: typeof order === 'number' ? { order } : {} },
    ).then((r) => r.question),

  /** Rejects it, with an optional note that reaches only the asker. */
  rejectQuestion: (quizId: string, askedQuestionId: string, rejectionReason?: string) =>
    apiRequest<{ question: AskedQuestion }>(
      `/quizzes/${quizId}/questions/${askedQuestionId}/reject`,
      { method: 'PUT', body: rejectionReason ? { rejectionReason } : {} },
    ).then((r) => r.question),

  /** Records what the presenter said. */
  answerQuestion: (quizId: string, askedQuestionId: string, answer: string) =>
    apiRequest<{ question: AskedQuestion }>(
      `/quizzes/${quizId}/questions/${askedQuestionId}/answer`,
      { method: 'PUT', body: { answer } },
    ).then((r) => r.question),

  /** Removes it for good. No push reaches the room; see questionDeletedEventFor. */
  deleteQuestion: (quizId: string, askedQuestionId: string) =>
    apiRequest<void>(`/quizzes/${quizId}/questions/${askedQuestionId}`, { method: 'DELETE' }),

  /** Puts an approved question on the projector, or takes the last one off. */
  presentQuestion: (quizId: string, askedQuestionId: string | null) =>
    apiRequest<{ presentedAskedQuestionId: string | null }>(
      `/quizzes/${quizId}/presentation/question`,
      { method: 'POST', body: { askedQuestionId } },
    ).then((r) => r.presentedAskedQuestionId),

  /**
   * Uploads straight to S3 with a presigned POST, so image bytes never pass
   * through the API. S3 enforces the type and size limits itself.
   *
   * `kind` only ever names the object key. The API allowlists the same four
   * (#75 widened the theme's two), so a name it does not know is a 400 rather
   * than a file quietly filed as a background.
   */
  async uploadAsset(quizId: string, kind: AssetKind, file: File): Promise<string> {
    const { upload, publicUrl } = await apiRequest<{
      upload: { url: string; fields: Record<string, string> };
      publicUrl: string;
    }>(`/quizzes/${quizId}/assets`, {
      method: 'POST',
      body: { kind, contentType: file.type },
    });

    const form = new FormData();
    for (const [key, value] of Object.entries(upload.fields)) form.append(key, value);
    form.append('file', file);

    const response = await fetch(upload.url, { method: 'POST', body: form });
    if (!response.ok) throw new Error('Upload was rejected. Check the file type and size.');

    return publicUrl;
  },
};
